import { createContext, useContext, useLayoutEffect } from 'react';

export interface PageConfig {
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '7xl' | 'full';
  /**
   * Width mode handed to building_blocks' LayoutProvider, which maps
   * `standard` to `max-w-7xl mx-auto px-4` and `full` to `w-full px-4`.
   *
   * In the building_blocks versions in use the only consumer is
   * `AppBreadcrumbs` -- the topbar and footer size themselves -- so this keeps
   * the breadcrumb bar from capping at 1280px while the content it labels runs
   * edge to edge. It does not itself widen the page; `maxWidth` does that.
   */
  layoutMode?: 'standard' | 'wide' | 'full';
  contentPadding?: 'none' | 'sm' | 'md' | 'lg';
  scrollable?: boolean;
  contentClassName?: string;
  mainClassName?: string;
  background?: 'default' | 'white' | 'gradient';
}

export interface PageConfigContextValue {
  pageConfig: PageConfig;
  setPageConfig: (config: PageConfig) => void;
  resetPageConfig: () => void;
}

export const DEFAULT_PAGE_CONFIG: PageConfig = {
  layoutMode: 'full',
  maxWidth: '7xl',
  contentPadding: 'none',
  contentClassName: 'flex-1 flex flex-col min-h-0 w-full min-w-0',
};

export const PageConfigContext = createContext<PageConfigContextValue | null>(null);

export function usePageConfig() {
  const context = useContext(PageConfigContext);
  if (!context) {
    throw new Error('usePageConfig must be used within PageConfigProvider');
  }
  return context;
}

/**
 * Hook for pages to override layout config. Uses useLayoutEffect to set
 * config before paint. Config is automatically reset when the page unmounts.
 */
export function useSetPageConfig(config: PageConfig) {
  const { setPageConfig, resetPageConfig } = usePageConfig();
  const configStr = JSON.stringify(config);

  useLayoutEffect(() => {
    setPageConfig(JSON.parse(configStr));
    return () => resetPageConfig();
  }, [configStr, setPageConfig, resetPageConfig]);
}
